import { FC, ReactNode, useState } from "react";
import { Asset, Network, networkLabel } from "rujira.js";
import { useTranslation } from "../../i18n";
import { IconDenom } from "../icons/IconDenom";
import { AngleRight, MinusCircle } from "../icons/Icons";
import { NetworkIcon } from "../icons/NetworkIcon";
import { Input } from "../inputs/Input";
import { Toggle } from "../inputs/Toggle";
import { Decimal } from "../numbers/Decimal";
import { Fiat } from "../numbers/Fiat";
import { filterAssetOverviewOptions } from "./filterAssetOverviewOptions";

export type AssetOverviewOption = {
  asset: Asset;
  amount?: bigint;
  valueUsd?: bigint;
  networks?: readonly Network[];
  secondary?: ReactNode;
  featured?: boolean;
  disabled?: boolean;
};

type AssetOverviewActionBase = {
  title: ReactNode;
  description: ReactNode;
  icon: ReactNode;
};

export type AssetOverviewAction = AssetOverviewActionBase &
  ({ href: string; onClick?: never } | { href?: never; onClick: () => void });

export interface AssetOverviewModalProps {
  title: string;
  description: string;
  searchPlaceholder: string;
  options: AssetOverviewOption[];
  topBar?: ReactNode;
  onSelect?: (asset: Asset) => void;
  toggleLabel?: string;
  initialShowAll?: boolean;
  emptyLabel?: string;
  optionsFooter?: ReactNode;
  actions?: AssetOverviewAction[];
}

const actionClassName =
  "asset-overview-modal__action transparent flex ai-c w-full p-1.5 br-1 text-left no-underline color-white overflow-hidden relative";

export const AssetOverviewActions: FC<{
  actions: AssetOverviewAction[];
  divided?: boolean;
  className?: string;
  onNavigate?: () => void;
}> = ({ actions, divided = true, className = "", onNavigate }) => {
  if (actions.length === 0) return null;

  const listClassName = [
    "flex dir-c gap-0.5",
    divided && "asset-overview-modal__actions mt-3 pt-2.5",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={listClassName}>
      {actions.map((action, index) => {
        const content = (
          <>
            <span className="asset-overview-modal__action-icon flex ai-c jc-c no-shrink w-5.5 h-5.5 br-1">
              {action.icon}
            </span>
            <span className="asset-overview-modal__copy flex dir-c ml-1">
              <span className="fs-14 fw-500">{action.title}</span>
              <span className="asset-overview-modal__action-description color-grey fs-12 mt-0.5">
                {action.description}
              </span>
            </span>
            <AngleRight className="color-grey no-shrink w-2 h-2 ml-a" />
          </>
        );

        return action.href ? (
          <a
            key={action.href}
            href={action.href}
            className={actionClassName}
            onClick={onNavigate}>
            {content}
          </a>
        ) : (
          <button
            key={index}
            type="button"
            className={actionClassName}
            onClick={() => {
              action.onClick?.();
              onNavigate?.();
            }}>
            {content}
          </button>
        );
      })}
    </div>
  );
};

export const AssetOverviewModal: FC<AssetOverviewModalProps> = ({
  title,
  description,
  searchPlaceholder,
  options,
  topBar,
  onSelect,
  toggleLabel,
  initialShowAll = false,
  emptyLabel,
  optionsFooter,
  actions = [],
}) => {
  const { t } = useTranslation("common");
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(initialShowAll);
  const filtered = filterAssetOverviewOptions(
    options,
    search,
    showAll,
    Boolean(toggleLabel),
    networkLabel
  );

  return (
    <div className="asset-overview-modal">
      {topBar}
      <h2 className="h3 mb-0">{title}</h2>
      <p className="p fs-14 lh-21 color-grey mt-1.5 mb-0">{description}</p>

      <div className="asset-overview-modal__controls">
        <Input
          containerClassName="grow"
          label={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}>
          {search && (
            <MinusCircle
              className="input-container__clear"
              onClick={() => setSearch("")}
            />
          )}
        </Input>
        {toggleLabel && (
          <Toggle
            className="toggle--xs color-white as-c"
            checked={showAll}
            aria-label={toggleLabel}
            onChange={(event) => setShowAll(event.currentTarget.checked)}>
            <span className="toggle__label">
              <span className="asset-overview-modal__toggle-label--desktop">
                {toggleLabel}
              </span>
              <span className="asset-overview-modal__toggle-label--mobile">
                {t("allAssets")}
              </span>
            </span>
          </Toggle>
        )}
      </div>

      <div className="asset-overview-modal__assets common-scrollbars">
        {filtered.map((option) => {
          const assetClassName = `card card--h-full p-1.5 flex ai-c color-white text-left w-full${
            option.disabled ? " asset-overview-modal__asset--disabled" : ""
          }`;
          const content = (
            <>
              <IconDenom
                denom={option.asset.metadata.symbol}
                className="block w-4 h-4 no-shrink"
              />
              <div className="asset-overview-modal__copy flex dir-c ml-1">
                <div className="asset-overview-modal__asset-line flex ai-c">
                  <span className="fw-600">{option.asset.metadata.symbol}</span>
                  {option.amount !== undefined && (
                    <Decimal
                      amount={option.amount}
                      decimals={option.asset.metadata.decimals}
                      round={6}
                      clip
                      className="ml-a"
                    />
                  )}
                </div>
                {(option.networks?.length ||
                  option.secondary ||
                  option.valueUsd !== undefined) && (
                  <div className="asset-overview-modal__asset-line asset-overview-modal__asset-line--secondary flex ai-c color-grey mt-0.5">
                    {option.networks?.map((network) => (
                      <NetworkIcon
                        key={network}
                        network={network}
                        className="w-2 h-2"
                      />
                    ))}
                    {option.secondary}
                    {option.valueUsd !== undefined && (
                      <Fiat
                        amount={option.valueUsd}
                        decimals={8}
                        symbol="$"
                        padSymbol={false}
                        clip
                        className="color-grey fs-13 ml-a"
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          );

          return (
            <div
              key={option.asset.asset}
              className="asset-overview-modal__asset-slot">
              {onSelect ? (
                <button
                  type="button"
                  disabled={option.disabled}
                  className={`${assetClassName}${
                    option.disabled ? "" : " card--hover-border"
                  }`}
                  onClick={() => onSelect(option.asset)}>
                  {content}
                </button>
              ) : (
                <div className={assetClassName}>{content}</div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && emptyLabel && (
          <p className="color-grey my-2 text-center">{emptyLabel}</p>
        )}
      </div>

      {optionsFooter && (
        <div className="asset-overview-modal__options-footer">
          {optionsFooter}
        </div>
      )}

      <AssetOverviewActions actions={actions} />
    </div>
  );
};
